/**
 * UI predicates for the Shopify integration card's "billing store" controls.
 *
 * Extracted from `components/integrations/shopify-integration-card.tsx` so the
 * multi-store / invalid-designation matrix can be unit-tested without rendering
 * the React component (mirrors `lib/billing/admin-change-plan-with-proration/ui-predicates.ts`).
 *
 * Designation is only meaningful when the tenant bills through Shopify; for a
 * Stripe/manual tenant the Shopify integration is catalog/attribution only.
 * Within a Shopify-billing tenant the controls surface when there is a real
 * choice OR a recovery is needed:
 *   - ≥2 eligible (active + token) stores → the merchant can choose which bills, OR
 *   - the currently-designated store is no longer eligible (resolver returns
 *     `pointer_invalid`, billing fails closed) AND ≥1 eligible alternative exists
 *     → the merchant must re-designate to recover.
 * A single eligible store with a healthy designation needs no control — the DB
 * trigger auto-designates the first install.
 */

import { BILLING_CHANNELS } from '@/lib/billing/constants'

export interface BillingStoreRow {
  is_billing_store?: boolean
  billing_eligible?: boolean
}

/** The designated billing store exists but can no longer bill (inactive / token-less). */
export function hasInvalidBillingStore(rows: BillingStoreRow[]): boolean {
  const designated = rows.find((r) => r.is_billing_store)
  return !!designated && designated.billing_eligible === false
}

/** Whether to surface the badge + "Set as billing store" controls at all. */
export function showBillingStoreControls(
  rows: BillingStoreRow[],
  billingChannel: string | null,
): boolean {
  if (billingChannel !== BILLING_CHANNELS.SHOPIFY) return false
  const eligibleCount = rows.filter((r) => r.billing_eligible).length
  if (eligibleCount >= 2) return true
  // Recovery path: the designated store broke and an eligible alternative exists.
  return hasInvalidBillingStore(rows) && eligibleCount >= 1
}
